/**
 * v0.5.0 「管理QQ会话」 card (Sessions page / 会话分页).
 *
 * QQ bot management is the narrow, explicitly approved exception to the
 * read-only discipline (DECISIONS.md IM-1). The daemon exposes loopback
 * `/qq/*` HTTP APIs and this card surfaces them without dressing failures up
 * as success.
 *
 * Safety rules encoded here:
 * - group chats are always `chat`; their approve/auto buttons stay disabled;
 * - `approve`/`auto` are offered only for the owner's private chat;
 * - switching to `approve`/`auto` goes through the app PIN helper when a PIN is
 *   configured, and `auto` additionally needs an explicit danger confirmation;
 * - 「断开」 needs a confirmation; every mutation waits for the daemon response
 *   (the card never optimistically flips a toggle).
 */
import { useEffect, useState } from 'react';
import { formatRelative } from '../lib/format';
import { nowSeconds } from '../lib/id';
import {
  QQ_CHAT_KIND_LABELS,
  QQ_MODES,
  QQ_MODE_DESCRIPTIONS,
  qqChatIdLabel,
  qqChatTitle,
  qqConnectionState,
  qqDisplayMode,
  qqModeChangeRequiresConfirm,
  qqModeChangeRequiresPin,
  qqModeDisabledHint,
  qqModeLabel,
  qqModeOptionEnabled,
  qqModePatch,
  qqModeRowHint,
  qqOwnerLabel,
  type QqChat,
  type QqConnectionTone,
  type QqMode,
  type QqStatus,
} from '../lib/qq';
import { useApp } from '../state/AppState';
import { ConfirmDialog } from './ConfirmDialog';
import { Icon } from './Icon';

const CONNECTION_PILL_CLASS: Record<QqConnectionTone, string> = {
  success: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200',
  warning: 'border-amber-400/30 bg-amber-400/10 text-amber-200',
  muted: 'border-slate-500/40 bg-slate-500/10 text-slate-300',
};

const CONNECTION_TEXT_CLASS: Record<QqConnectionTone, string> = {
  success: 'text-emerald-300',
  warning: 'text-amber-300',
  muted: 'text-slate-400',
};

function ChatRow({
  status,
  chat,
  busy,
  onSelect,
}: {
  status: QqStatus;
  chat: QqChat;
  busy: boolean;
  onSelect(chat: QqChat, mode: QqMode): void;
}) {
  const current = qqDisplayMode(status, chat);
  const hint = qqModeRowHint(status, chat);
  return (
    <li
      data-testid="qq-chat-row"
      data-key={chat.key}
      className="rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2.5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="break-words text-xs text-slate-200">
            {qqChatTitle(chat)}
            <span className="ml-1.5 rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[9px] text-slate-400">
              {QQ_CHAT_KIND_LABELS[chat.kind]}
            </span>
          </p>
          <p className="mt-0.5 break-all font-mono text-[10px] text-slate-500">
            {qqChatIdLabel(chat)} · 更新于 {formatRelative(chat.updated_at, nowSeconds())}
          </p>
        </div>
        <div
          role="group"
          aria-label={`${qqChatTitle(chat)} 权限档位`}
          className="flex shrink-0 gap-1"
        >
          {QQ_MODES.map((mode) => {
            const selected = current === mode;
            const enabled = qqModeOptionEnabled(status, chat, mode);
            const title = !enabled
              ? qqModeDisabledHint(status, chat, mode)
              : mode === 'auto'
                ? (hint ?? QQ_MODE_DESCRIPTIONS.auto)
                : QQ_MODE_DESCRIPTIONS[mode];
            return (
              <button
                key={mode}
                type="button"
                data-testid={`qq-mode-${mode}`}
                aria-pressed={selected}
                disabled={busy || !enabled}
                title={title}
                onClick={() => onSelect(chat, mode)}
                className={`rounded-lg border px-2 py-1 text-[10px] transition ${
                  selected
                    ? false
                      ? 'border-dashed border-amber-400/50 bg-amber-400/5 text-amber-200/80'
                      : 'border-indigo-400/60 bg-indigo-400/20 text-indigo-100'
                    : 'border-white/10 bg-white/5 text-slate-300'
                } disabled:opacity-35`}
              >
                {qqModeLabel(mode)}
              </button>
            );
          })}
        </div>
      </div>
      {hint ? <p className="mt-1.5 text-[10px] leading-relaxed text-slate-500">{hint}</p> : null}
    </li>
  );
}

export function QqSessionsCard() {
  const {
    settings,
    pinRecord,
    verifyForAction,
    qqAvailable,
    qqUnavailableReason,
    qqStatus,
    qqLoading,
    qqError,
    refreshQq,
    setQqPermissions,
    disconnectQq,
  } = useApp();
  const [open, setOpen] = useState(false);
  const [modeConfirm, setModeConfirm] = useState<{ chat: QqChat; next: QqMode } | null>(null);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [groupDraft, setGroupDraft] = useState('');
  const [groupTitle, setGroupTitle] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const busy = qqLoading;
  const connection = qqConnectionState(qqStatus);

  // Load once as soon as the transport is available; the card shows its real
  // daemon state even while collapsed (never a placeholder “connected”).
  useEffect(() => {
    if (qqAvailable) void refreshQq();
  }, [qqAvailable, refreshQq]);

  const toggleOpen = (): void => {
    setOpen((value) => {
      const next = !value;
      if (next && qqAvailable) void refreshQq();
      return next;
    });
  };

  const applyMode = async (chat: QqChat, next: QqMode, pin?: string): Promise<boolean> => {
    setActionError(null);
    if (qqModeChangeRequiresPin(next)) {
      // TODO(v0.5.0): reuse a shared inline PIN prompt when one exists; today
      // only the settings page verifies PINs, so the card asks locally and
      // falls back to the confirmation when no PIN is configured.
      const verified = await verifyForAction(pin);
      if (!verified.ok) {
        setActionError(verified.error ?? '需要重新验证 PIN');
        return false;
      }
    }
    const patch = qqModePatch(qqStatus, chat, next);
    if (patch === null) {
      setActionError('该档位当前不可用');
      return false;
    }
    const result = await setQqPermissions(patch);
    if (!result.ok) {
      setActionError(result.error ?? '切换失败');
      return false;
    }
    return true;
  };

  const requestMode = (chat: QqChat, next: QqMode): void => {
    if (busy || qqStatus === null) return;
    if (!qqModeOptionEnabled(qqStatus, chat, next)) return;
    if (qqDisplayMode(qqStatus, chat) === next) return;
    setActionError(null);
    if (qqModeChangeRequiresConfirm(chat, next) || qqModeChangeRequiresPin(next)) {
      setPinInput('');
      setModeConfirm({ chat, next });
      return;
    }
    void applyMode(chat, next);
  };

  const confirmMode = async (): Promise<void> => {
    if (modeConfirm === null || busy) return;
    const succeeded = await applyMode(modeConfirm.chat, modeConfirm.next, pinInput);
    if (succeeded) {
      setModeConfirm(null);
      setPinInput('');
    }
  };

  const confirmDisconnect = async (): Promise<void> => {
    if (busy) return;
    setActionError(null);
    const result = await disconnectQq();
    if (result.ok) setDisconnectConfirm(false);
    else setActionError(result.error ?? '断开失败');
  };

  const toggleEnabled = async (): Promise<void> => {
    if (qqStatus === null || busy) return;
    setActionError(null);
    const result = await setQqPermissions({ enabled: !qqStatus.enabled });
    if (!result.ok) setActionError(result.error ?? '切换启用状态失败');
  };

  const addGroup = async (): Promise<void> => {
    const id = groupDraft.trim();
    if (!/^\d{5,12}$/u.test(id)) {
      setActionError('群号必须是 5–12 位数字');
      return;
    }
    if (busy || addingGroup) return;
    setActionError(null);
    setAddingGroup(true);
    try {
      const title = groupTitle.trim();
      const result = await setQqPermissions({
        mode: { kind: 'group', id, mode: 'chat', ...(title.length === 0 ? {} : { title }) },
      });
      if (!result.ok) {
        setActionError(result.error ?? '添加群聊失败');
        return;
      }
      setGroupDraft('');
      setGroupTitle('');
    } finally {
      setAddingGroup(false);
    }
  };

  const refresh = (): void => {
    setActionError(null);
    void refreshQq();
  };

  const headerLabel = qqAvailable ? connection.label : '未接入';
  const headerTone: QqConnectionTone = qqAvailable ? connection.tone : 'muted';

  return (
    <section
      data-testid="qq-sessions-card"
      data-open={open ? 'true' : 'false'}
      className="rounded-2xl border border-white/10 bg-[#0f1629]"
    >
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <Icon name="chat" className="h-4 w-4 shrink-0 text-sky-300" />
          <span className="text-xs font-medium text-slate-200">管理QQ会话</span>
          <span
            data-testid="qq-status-label"
            className={`rounded-full border px-1.5 py-0.5 text-[10px] ${CONNECTION_PILL_CLASS[headerTone]}`}
          >
            {headerLabel}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5 text-[10px] text-slate-500">
          {qqStatus?.owner_id ? <span className="font-mono">owner {qqStatus.owner_id}</span> : null}
          <span>{open ? '收起' : '展开'}</span>
          <Icon
            name="chevron"
            className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`}
          />
        </span>
      </button>

      {open ? (
        <div className="space-y-2.5 border-t border-white/5 px-3 py-3">
          {!qqAvailable ? (
            <div
              data-testid="qq-unavailable"
              className="rounded-xl border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200"
            >
              {qqUnavailableReason ?? 'QQ 管理当前不可用'}。此卡片不会伪造状态，也不会发送任何操作。
            </div>
          ) : qqStatus === null ? (
            <div className="space-y-2">
              {qqError ? (
                <div
                  data-testid="qq-error"
                  className="rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2 text-[11px] leading-relaxed text-rose-200"
                >
                  <p className="break-words">{qqError}</p>
                </div>
              ) : (
                <p className="px-1 py-1 text-[11px] text-slate-500">
                  {busy ? '正在读取 QQ bot 状态…' : '尚未加载 QQ bot 状态。'}
                </p>
              )}
              <button
                type="button"
                data-testid="qq-refresh"
                disabled={busy}
                onClick={refresh}
                className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300 disabled:opacity-40"
              >
                {busy ? '读取中…' : '读取 QQ 状态'}
              </button>
            </div>
          ) : (
            <>
              {qqError ? (
                <div
                  data-testid="qq-error"
                  className="rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2 text-[11px] leading-relaxed text-rose-200"
                >
                  <p className="break-words">{qqError}</p>
                  <button
                    type="button"
                    onClick={refresh}
                    disabled={busy}
                    className="mt-2 rounded-lg border border-rose-400/40 bg-rose-400/10 px-2.5 py-1 text-[11px] text-rose-100 disabled:opacity-40"
                  >
                    重试
                  </button>
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2">
                  <p className="text-[10px] text-slate-500">连接状态</p>
                  <p className={`mt-0.5 ${CONNECTION_TEXT_CLASS[connection.tone]}`}>
                    {connection.label}
                  </p>
                </div>
                <div className="rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2">
                  <p className="text-[10px] text-slate-500">Owner QQ</p>
                  <p className="mt-0.5 truncate font-mono text-slate-200">
                    {qqOwnerLabel(qqStatus)}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2">
                <div className="min-w-0">
                  <p className="text-[11px] text-slate-300">QQ bot 总开关</p>
                  <p className="mt-0.5 text-[10px] leading-relaxed text-slate-500">
                    {qqStatus.enabled
                      ? '已启用：daemon 会连接 OneBot/NapCat；群聊永远聊天档。'
                      : '未启用：不会连接 QQ，也不会有任何自动回复。'}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={qqStatus.enabled}
                  data-testid="qq-enabled-toggle"
                  disabled={busy}
                  onClick={() => void toggleEnabled()}
                  className={`flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[11px] disabled:opacity-40 ${
                    qqStatus.enabled
                      ? 'border-emerald-400/40 bg-emerald-400/15 text-emerald-100'
                      : 'border-white/10 bg-white/5 text-slate-300'
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      qqStatus.enabled ? 'bg-emerald-400' : 'bg-slate-500'
                    }`}
                  />
                  {qqStatus.enabled ? '已启用' : '未启用'}
                </button>
              </div>

              <div className="flex items-center justify-between gap-2">
                <h3 className="text-[11px] font-medium text-slate-300">会话档位</h3>
                <span className="flex items-center gap-1.5">
                  <button
                    type="button"
                    data-testid="qq-refresh"
                    disabled={busy}
                    onClick={refresh}
                    className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300 disabled:opacity-40"
                  >
                    <Icon name="refresh" className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} />
                    刷新
                  </button>
                  <button
                    type="button"
                    data-testid="qq-disconnect"
                    disabled={busy || !qqStatus.connected}
                    onClick={() => {
                      setActionError(null);
                      setDisconnectConfirm(true);
                    }}
                    title={qqStatus.connected ? '停止 QQ bot 连接' : '当前未连接'}
                    className="rounded-lg border border-rose-400/30 bg-rose-400/10 px-2.5 py-1 text-[11px] text-rose-100 disabled:opacity-40"
                  >
                    断开
                  </button>
                </span>
              </div>

              {actionError && modeConfirm === null && !disconnectConfirm ? (
                <p className="rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2 text-[11px] leading-relaxed text-rose-200">
                  {actionError}
                </p>
              ) : null}

              <div className="space-y-1.5 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2">
                <p className="text-[11px] text-slate-300">添加群聊</p>
                <p className="text-[10px] leading-relaxed text-slate-500">
                  加白名单后，群里只有 @bot 才会回复；群聊永远是聊天档。
                </p>
                <div className="flex flex-wrap items-center gap-1.5">
                  <input
                    value={groupDraft}
                    onChange={(event) =>
                      setGroupDraft(event.target.value.replace(/[^0-9]/gu, '').slice(0, 12))
                    }
                    inputMode="numeric"
                    placeholder="QQ 群号"
                    aria-label="QQ 群号"
                    className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-slate-200 outline-none placeholder:text-slate-600"
                  />
                  <input
                    value={groupTitle}
                    onChange={(event) => setGroupTitle(event.target.value.slice(0, 40))}
                    placeholder="备注（可选）"
                    aria-label="群备注"
                    className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-slate-200 outline-none placeholder:text-slate-600"
                  />
                  <button
                    type="button"
                    data-testid="qq-add-group"
                    disabled={busy || addingGroup || groupDraft.trim().length === 0}
                    onClick={() => void addGroup()}
                    className="rounded-lg border border-sky-400/30 bg-sky-400/10 px-2.5 py-1 text-[11px] text-sky-100 disabled:opacity-40"
                  >
                    {addingGroup ? '添加中…' : '添加'}
                  </button>
                </div>
              </div>

              {qqStatus.chats.length === 0 ? (
                <p className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 text-[11px] leading-relaxed text-slate-500">
                  暂无 QQ 会话。NapCat 登录并收到白名单私聊/群消息后，daemon 会在这里列出对应档位。
                </p>
              ) : (
                <ul className="space-y-2">
                  {qqStatus.chats.map((chat) => (
                    <ChatRow
                      key={chat.key}
                      status={qqStatus}
                      chat={chat}
                      busy={busy}
                      onSelect={requestMode}
                    />
                  ))}
                </ul>
              )}

              {settings.demoMode ? (
                <p className="flex items-start gap-1 text-[10px] leading-relaxed text-sky-200/80">
                  <Icon name="spark" className="mt-0.5 h-3 w-3 shrink-0" />
                  演示模式：状态来自本地 mock，不连接真实 QQ，也不会真正执行任何工具。
                </p>
              ) : null}

              <ul className="list-disc space-y-1 pl-4 text-[10px] leading-relaxed text-slate-500">
                <li>群聊永远聊天档；只有 owner 的私聊可切换审批档。</li>
                <li>
                  审批档需 PIN 验证（未设置 PIN 时以确认代替）；auto 档切好后，还要在 QQ 私聊
                  发送“确认开启 D 档”才会自动执行，发“确认关闭 D 档”解除。
                </li>
                <li>QQ 消息默认不可信；所有收发与工具执行入审计，可在本卡片立即断开。</li>
              </ul>
            </>
          )}
        </div>
      ) : null}

      <ConfirmDialog
        open={modeConfirm !== null}
        title={modeConfirm?.next === 'auto' ? '切换为「自动」档？' : '切换为「审批」档？'}
        danger={modeConfirm?.next === 'auto'}
        busy={busy}
        confirmLabel="确认切换"
        disabled={pinRecord !== null && pinInput.length === 0}
        onCancel={() => {
          if (busy) return;
          setModeConfirm(null);
          setPinInput('');
          setActionError(null);
        }}
        onConfirm={() => void confirmMode()}
      >
        {modeConfirm !== null && qqStatus !== null ? (
          <>
            <p className="text-[11px] leading-relaxed text-slate-400">
              {qqChatTitle(modeConfirm.chat)}（{qqChatIdLabel(modeConfirm.chat)}）将从「
              {qqModeLabel(qqDisplayMode(qqStatus, modeConfirm.chat))}」切换为「
              {qqModeLabel(modeConfirm.next)}」：{QQ_MODE_DESCRIPTIONS[modeConfirm.next]}。
            </p>
            {modeConfirm.next === 'auto' ? (
              <p className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200">
                切到 auto 后，请在 QQ 私聊发送“确认开启 D 档”武装自动执行； 发“确认关闭 D
                档”解除。武装期间写操作不再走手机审批，但风险引擎仍会硬拒绝危险命令。
              </p>
            ) : null}
            {pinRecord !== null ? (
              <label className="flex flex-col gap-1 text-[11px] text-slate-400">
                输入 PIN 以确认高危切换
                <input
                  value={pinInput}
                  onChange={(event) =>
                    setPinInput(event.target.value.replace(/[^0-9]/gu, '').slice(0, 8))
                  }
                  type="password"
                  inputMode="numeric"
                  autoFocus
                  aria-label="QQ 档位切换 PIN"
                  className="rounded-lg border border-white/10 bg-[#0f1629] px-2.5 py-2 text-center text-sm tracking-[0.3em] text-slate-100 outline-none focus:border-indigo-400/60"
                />
              </label>
            ) : (
              <p className="text-[10px] leading-relaxed text-slate-500">
                未设置 PIN：当前以确认对话框代替；建议在设置中启用 PIN 后再使用审批/自动档。
              </p>
            )}
            {actionError ? <p className="text-[11px] text-rose-300">{actionError}</p> : null}
          </>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={disconnectConfirm}
        title="断开 QQ bot？"
        danger
        busy={busy}
        confirmLabel="断开"
        onCancel={() => {
          if (busy) return;
          setDisconnectConfirm(false);
          setActionError(null);
        }}
        onConfirm={() => void confirmDisconnect()}
      >
        <p className="text-[11px] leading-relaxed text-slate-400">
          将停止 daemon 与 OneBot/NapCat 的连接；断开后 QQ 消息不会再触发任何回复或工具执行。
          档位与启用设置保留。
        </p>
        {actionError ? <p className="text-[11px] text-rose-300">{actionError}</p> : null}
      </ConfirmDialog>
    </section>
  );
}
